import React from 'react';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';

function NutriScalePrivacy() {
  const sections = [
    {
      title: 'Introduction',
      content: (
        <>
          <p>
            Castle Point Innovations ("we," "us," or "our") operates the NutriScale mobile
            application and compatible Bluetooth food scale hardware. NutriScale helps you weigh
            food items, retrieve nutritional information, and track your dietary intake over time.
          </p>
          <p>
            Because NutriScale handles sensitive health and nutrition data, we are committed to
            transparency about how your information is collected, used, and protected. This Privacy
            Policy explains our data practices and your rights regarding your personal information.
          </p>
          <p>
            By using NutriScale, you agree to the collection and use of information as described in
            this policy. If you do not agree, please discontinue use of the app and associated
            hardware.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect the following categories of information:</p>

          <p><strong>Account Data</strong></p>
          <ul>
            <li>Email address and display name (used for account creation and identification)</li>
            <li>Authentication credentials (stored securely using industry-standard hashing)</li>
            <li>User-configured preferences and dietary goals</li>
          </ul>

          <p><strong>Health and Nutrition Data</strong></p>
          <ul>
            <li>Food item names, weights, and calculated nutritional values (calories, macronutrients, micronutrients)</li>
            <li>Daily dietary intake logs and meal history</li>
            <li>Nutrition data retrieved from the Open Food Facts API and USDA FoodData Central API</li>
            <li>Barcode scan results used to identify packaged food products</li>
            <li>Data read from or written to Apple HealthKit or Google Fit, only when you explicitly enable this integration</li>
          </ul>

          <p><strong>Device and Hardware Data</strong></p>
          <ul>
            <li>Bluetooth Low Energy (BLE) pairing and connection data for your NutriScale hardware</li>
            <li>Scale calibration data and firmware version information</li>
            <li>WiFi connection credentials, which are stored exclusively on the device firmware (ESP32-S3) and are never transmitted to our servers</li>
            <li>Device identifiers used to associate your scale with your account</li>
          </ul>

          <p><strong>Usage Data</strong></p>
          <ul>
            <li>App usage analytics such as feature usage frequency, session duration, and navigation patterns</li>
            <li>Crash reports and error logs to help us improve app stability</li>
            <li>Device type, operating system version, and app version</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Health Data',
      content: (
        <>
          <p>
            We recognize that health and nutrition data is among the most sensitive categories of
            personal information. NutriScale is designed with a local-first approach to protect this
            data:
          </p>
          <ul>
            <li>
              <strong>Local storage by default.</strong> Your food logs, nutritional calculations,
              and dietary history are stored locally on your device in a SQLite database. This data
              does not leave your device unless you explicitly choose to sync or back it up.
            </li>
            <li>
              <strong>HealthKit and Google Fit integration is entirely optional.</strong> NutriScale
              will never read from or write to Apple HealthKit or Google Fit without your explicit
              permission. You can enable or disable this integration at any time in the app settings,
              and you may revoke access through your device's system settings.
            </li>
            <li>
              <strong>You control your health data.</strong> You can view, export, or delete all
              health and nutrition data stored by NutriScale at any time from within the app.
            </li>
            <li>
              <strong>No selling of health data.</strong> We will never sell, rent, or trade your
              health or nutrition data to third parties for advertising or marketing purposes.
            </li>
          </ul>
          <p>
            When you perform a nutrition lookup, the app sends food identifiers (such as barcodes or
            search terms) to third-party nutrition databases to retrieve nutritional information. The
            returned data is cached locally on your device to minimize future network requests.
          </p>
        </>
      ),
    },
    {
      title: 'Device Permissions',
      content: (
        <>
          <p>NutriScale requests the following device permissions, each for a specific purpose:</p>
          <ul>
            <li>
              <strong>Bluetooth</strong> — Required to connect to and communicate with your
              NutriScale food scale hardware via BLE.
            </li>
            <li>
              <strong>Health app access (Apple HealthKit / Google Fit)</strong> — Optional. Allows
              NutriScale to read and write nutrition data to your device's health platform. You are
              prompted before access is granted and can revoke it at any time.
            </li>
            <li>
              <strong>Camera</strong> — Used for barcode scanning to identify packaged food products
              and retrieve their nutritional information. Camera data is processed on-device and is
              not stored or transmitted.
            </li>
            <li>
              <strong>Local storage</strong> — Used to maintain a local SQLite database of your food
              logs, preferences, and cached nutrition data.
            </li>
          </ul>
          <p>
            You can manage all permissions through your device's system settings. Disabling a
            permission may limit certain features of the app.
          </p>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide core app functionality, including food weighing, nutritional lookup, and dietary tracking</li>
            <li>Maintain and authenticate your user account</li>
            <li>Pair and communicate with your NutriScale hardware device</li>
            <li>Retrieve nutritional data from third-party databases on your behalf</li>
            <li>Sync nutrition data with Apple HealthKit or Google Fit when you have opted in</li>
            <li>Improve app performance, fix bugs, and develop new features based on aggregated, anonymized usage analytics</li>
            <li>Communicate with you regarding account-related matters, support requests, or important product updates</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Third-Party Services',
      content: (
        <>
          <p>NutriScale integrates with the following third-party services:</p>
          <ul>
            <li>
              <strong>Open Food Facts API</strong> — An open-source nutrition database used to
              retrieve nutritional information for packaged food products identified by barcode or
              name. When you perform a lookup, the barcode or search query is sent to Open Food
              Facts. Their privacy policy governs data they receive.
            </li>
            <li>
              <strong>USDA FoodData Central (FDC) API</strong> — A U.S. government nutrition
              database used to retrieve detailed nutritional data. Search queries are sent to the
              USDA API to fulfill your requests.
            </li>
            <li>
              <strong>Apple HealthKit</strong> — When enabled by you on iOS, NutriScale may read and
              write nutrition-related data to HealthKit. This data is governed by Apple's privacy
              policies and is stored securely on your device by Apple.
            </li>
            <li>
              <strong>Google Fit</strong> — When enabled by you on Android, NutriScale may read and
              write nutrition-related data to Google Fit. This data is governed by Google's privacy
              policies.
            </li>
          </ul>
          <p>
            We do not share your personal data with any third party beyond what is necessary to
            provide the services described above. We do not integrate third-party advertising
            networks.
          </p>
        </>
      ),
    },
    {
      title: 'Data Storage and Security',
      content: (
        <>
          <p>
            NutriScale employs a local-first architecture. The majority of your data, including food
            logs, nutritional calculations, and dietary history, is stored in a local SQLite database
            on your device. Shared preferences are used to store non-sensitive app settings.
          </p>
          <p>We protect your data through the following measures:</p>
          <ul>
            <li>Account credentials are hashed and stored using industry-standard encryption</li>
            <li>Communication between the app and our servers uses TLS encryption</li>
            <li>BLE communication with the NutriScale hardware uses standard Bluetooth security protocols</li>
            <li>WiFi credentials stored on the ESP32-S3 firmware are kept in device-local non-volatile storage and are never transmitted externally</li>
            <li>Health data accessed through HealthKit or Google Fit is subject to the security controls enforced by those platforms</li>
          </ul>
          <p>
            While we take reasonable precautions to protect your information, no method of electronic
            storage or transmission is completely secure. We cannot guarantee absolute security.
          </p>
        </>
      ),
    },
    {
      title: 'Data Retention',
      content: (
        <>
          <p>
            Locally stored data (food logs, nutrition cache, preferences) remains on your device
            until you delete it through the app or uninstall the application.
          </p>
          <p>
            Account data is retained for as long as your account is active. If you request account
            deletion, we will remove your account data from our servers within 30 days. Locally
            stored data must be deleted separately by you from your device.
          </p>
          <p>
            Anonymized, aggregated usage analytics may be retained indefinitely to support product
            improvement.
          </p>
        </>
      ),
    },
    {
      title: 'Your Rights',
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the following rights regarding your data:</p>
          <ul>
            <li><strong>Access</strong> — You may request a copy of the personal data we hold about you.</li>
            <li><strong>Correction</strong> — You may request correction of inaccurate personal data.</li>
            <li><strong>Deletion</strong> — You may request deletion of your account and associated server-side data. Locally stored data can be deleted directly from the app or by uninstalling it.</li>
            <li><strong>Portability</strong> — You may export your food logs and nutrition data from within the app.</li>
            <li><strong>Withdraw consent</strong> — You may revoke HealthKit or Google Fit access at any time through your device settings or the NutriScale app settings.</li>
            <li><strong>Opt out of analytics</strong> — You may disable usage analytics from the app settings.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at the address provided below.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <>
          <p>
            NutriScale is not directed at children under the age of 13. We do not knowingly collect
            personal information from children under 13. If you believe that a child under 13 has
            provided us with personal data, please contact us and we will take steps to delete such
            information promptly.
          </p>
        </>
      ),
    },
    {
      title: 'Changes to This Policy',
      content: (
        <>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices
            or applicable regulations. When we make material changes, we will notify you through the
            app or via email prior to the changes taking effect. The "Last Updated" date at the top
            of this policy indicates when the most recent revision was published.
          </p>
          <p>
            Your continued use of NutriScale after any changes to this policy constitutes acceptance
            of the updated terms.
          </p>
        </>
      ),
    },
    {
      title: 'Contact Us',
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our
            data practices, please contact us at:
          </p>
          <p>
            <strong>Castle Point Innovations</strong>
            <br />
            Email: <a href="mailto:contact@castlepointinnovations.com">contact@castlepointinnovations.com</a>
          </p>
        </>
      ),
    },
  ];

  return <PrivacyPolicy productName="NutriScale" lastUpdated="March 27, 2026" sections={sections} />;
}

export default NutriScalePrivacy;
